import { useMemo } from "react";

import { EOM_DATE_STR } from "app/utils/date";
import { percentChanged, safeParseFloat, safeParseInt } from "app/utils/math";

import { getCostFieldName, transformSortByToCSO } from "./filterTransformUtils";
import { useCostReportingConfig } from "./useCostReportingConfig";
import { useCostSavingOpportunity } from "./useCostSavingOpportunity";
import { useCostSavingOpportunityTag } from "./useCostSavingOpportunityTag";

import type { CostSavingOpportunityResult } from "./useCostSavingOpportunity.schema";
import type { CostSavingOpportunityTagResult } from "./useCostSavingOpportunityTag.schema";
import type { CloudCostType } from "app/contexts/UserContext";
import type { ActionBarSortBy } from "app/views/dashboards/utils/costDashboardActionBar";
import type { FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";
import type { SavingsResults } from "app/views/dashboards/utils/savings";

export interface ProcessedOpportunitiesResults extends SavingsResults {
  sectionKey: string;
}

/**
 * Extracts and parses savings and count values from a data item
 */
function extractSavingsAndCount(
  item: CostSavingOpportunityResult | CostSavingOpportunityTagResult,
  costType: CloudCostType | undefined,
  isByTag: boolean,
  prefix: string,
): { savings: number; count: number } {
  const savings = safeParseFloat(getCostField(item, costType, isByTag));
  const count = safeParseInt(item[`${prefix}.nonCompliantResourceCount`]);
  return { savings, count };
}

/**
 * Helper function to extract the appropriate cost field value from a data item
 *
 * Uses getCostFieldName to determine the field name, then extracts the value from the item
 */
function getCostField(
  item: CostSavingOpportunityResult | CostSavingOpportunityTagResult,
  costType: CloudCostType | undefined,
  isByTag: boolean,
): string | undefined | null {
  const prefix = isByTag ? "CSOTag" : "CSO";
  const fieldName = getCostFieldName(costType, prefix);
  return item[fieldName];
}

/**
 * Helper function to sort processed results since aggregating client-side may change the ranking of items
 *
 * Maps sortBy field names to ProcessedOpportunitiesResults fields
 */
function sortProcessedResults(
  results: ProcessedOpportunitiesResults[],
  sortBy: ActionBarSortBy | undefined,
): ProcessedOpportunitiesResults[] {
  if (!sortBy || results.length === 0) {
    return results;
  }

  const [member, direction] = sortBy;
  const isDescending = direction === "desc";

  // Determine which field to sort by based on the sortBy member
  if (member.includes("monthlySavingOpportunity")) {
    return [...results].sort((a, b) => {
      const diff = a.savings - b.savings;
      return isDescending ? -diff : diff;
    });
  }

  if (member.includes("nonCompliantResourceCount")) {
    return [...results].sort((a, b) => {
      const diff = a.count - b.count;
      return isDescending ? -diff : diff;
    });
  }

  // Default: sort by savings descending if sortBy doesn't match known fields
  return [...results].sort((a, b) => b.savings - a.savings);
}

/**
 * Helper function to process section data with aggregation
 *
 * Aggregates data by section key to keep unique opportunities when account filters are applied
 * Results are sorted after aggregation to preserve user-requested sort order
 */
function processAggregatedSectionData(
  currData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  prevData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  costType: CloudCostType | undefined,
  isByTag: boolean,
  sortBy: ActionBarSortBy | undefined,
): ProcessedOpportunitiesResults[] {
  if (!currData || currData.length === 0) {
    return [];
  }

  const prefix = isByTag ? "CSOTag" : "CSO";

  const currentAggregated = new Map<
    string,
    { savings: number; count: number }
  >();
  const previousAggregated = new Map<
    string,
    { savings: number; count: number }
  >();

  // Aggregate current data by sectionKey
  for (const item of currData) {
    const sectionKey = item[`${prefix}.section_key`];
    if (!sectionKey) continue;

    const { savings, count } = extractSavingsAndCount(
      item,
      costType,
      isByTag,
      prefix,
    );

    const existing = currentAggregated.get(sectionKey);
    if (existing) {
      existing.savings += savings;
      existing.count += count;
    } else {
      currentAggregated.set(sectionKey, { savings, count });
    }
  }

  // Aggregate previous data by sectionKey
  if (prevData) {
    for (const previous of prevData) {
      const sectionKey = previous[`${prefix}.section_key`];
      if (!sectionKey) continue;

      const { savings, count } = extractSavingsAndCount(
        previous,
        costType,
        isByTag,
        prefix,
      );

      const existing = previousAggregated.get(sectionKey);
      if (existing) {
        existing.savings += savings;
        existing.count += count;
      } else {
        previousAggregated.set(sectionKey, { savings, count });
      }
    }
  }

  // Calculate results from aggregated data
  const results: ProcessedOpportunitiesResults[] = [];
  for (const [sectionKey, current] of currentAggregated.entries()) {
    const previous = previousAggregated.get(sectionKey);

    results.push({
      sectionKey: String(sectionKey),
      savings: current.savings,
      savingsDiff: previous
        ? percentChanged(current.savings, previous.savings)
        : null,
      count: current.count,
      countDiff: previous
        ? percentChanged(current.count, previous.count)
        : null,
    });
  }

  // Sort results after aggregation to preserve user-requested sort order
  // Aggregation may change the ranking of items, so we need to re-sort
  return sortProcessedResults(results, sortBy);
}

/**
 * Helper function to process section data without aggregation
 *
 * Processes each item individually, matching current and previous data by section key
 */
function processNonAggregatedSectionData(
  currData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  prevData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  costType: CloudCostType | undefined,
  isByTag: boolean,
): ProcessedOpportunitiesResults[] {
  if (!currData || currData.length === 0) {
    return [];
  }

  const prefix = isByTag ? "CSOTag" : "CSO";

  // Build a map of previous data by sectionKey for O(1) lookups
  const previousMap = new Map<
    string,
    CostSavingOpportunityResult | CostSavingOpportunityTagResult
  >();
  if (prevData) {
    for (const previous of prevData) {
      const sectionKey = previous[`${prefix}.section_key`];
      if (sectionKey) {
        // Use the first occurrence if there are duplicates
        if (!previousMap.has(sectionKey)) {
          previousMap.set(sectionKey, previous);
        }
      }
    }
  }

  const results: ProcessedOpportunitiesResults[] = [];

  for (const item of currData) {
    const sectionKey = item[`${prefix}.section_key`];
    if (!sectionKey) continue;

    const { savings: currentSavings, count: currentNonCompliantResourceCount } =
      extractSavingsAndCount(item, costType, isByTag, prefix);

    const previous = previousMap.get(sectionKey);

    if (previous) {
      const {
        savings: previousSavings,
        count: previousNonCompliantResourceCount,
      } = extractSavingsAndCount(previous, costType, isByTag, prefix);

      const diffSavings = percentChanged(currentSavings, previousSavings);
      const diffNonCompliantResourceCount = percentChanged(
        currentNonCompliantResourceCount,
        previousNonCompliantResourceCount,
      );

      results.push({
        sectionKey: String(sectionKey),
        savings: currentSavings,
        savingsDiff: diffSavings,
        count: currentNonCompliantResourceCount,
        countDiff: diffNonCompliantResourceCount,
      });
    } else {
      results.push({
        sectionKey: String(sectionKey),
        savings: currentSavings,
        savingsDiff: null,
        count: currentNonCompliantResourceCount,
        countDiff: null,
      });
    }
  }

  return results;
}

/**
 * Helper function to process section data
 *
 * Dispatches to the appropriate processing function based on aggregation mode
 */
function processSectionData(
  currData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  prevData:
    | CostSavingOpportunityResult[]
    | CostSavingOpportunityTagResult[]
    | null,
  costType: CloudCostType | undefined,
  isByTag: boolean,
  aggregateBySectionKey = false,
  sortBy?: ActionBarSortBy,
): ProcessedOpportunitiesResults[] {
  if (!currData || currData.length === 0) {
    return [];
  }

  return aggregateBySectionKey
    ? processAggregatedSectionData(
        currData,
        prevData,
        costType,
        isByTag,
        sortBy,
      )
    : processNonAggregatedSectionData(currData, prevData, costType, isByTag);
}

/**
 * Hook for fetching cost saving opportunities aggregated by sections within a dashboard
 * Uses the new CSO cube via useCostSavingOpportunity and useCostSavingOpportunityTag hooks
 *
 * Replaces useOpportunitiesSections with CSO cube support
 */
export function useOpportunitiesSectionsCost(
  dashboardKey: string,
  filters?: FilterBarCubeFilters,
  sortBy?: ActionBarSortBy,
  isResourceFiltering = false,
  aggregateBySectionKey = false,
): {
  results: ProcessedOpportunitiesResults[] | null;
  isLoading: boolean;
  error: Error | null;
} {
  const costType = useCostReportingConfig();

  // Transform sortBy to CSO format
  const order = transformSortByToCSO(sortBy, costType, isResourceFiltering);

  // Query without tag filtering
  const {
    data: currDataNoTag,
    isLoading: isLoadingCurrNoTag,
    error: errorCurrNoTag,
  } = useCostSavingOpportunity({
    dashboardKey,
    dateRange: "Yesterday",
    filters,
    dashboardSummary: false,
    includeSectionDimension: true,
    order,
    enabled: !isResourceFiltering,
  });

  const {
    data: prevDataNoTag,
    isLoading: isLoadingPrevNoTag,
    error: errorPrevNoTag,
  } = useCostSavingOpportunity({
    dashboardKey,
    dateRange: [EOM_DATE_STR, EOM_DATE_STR],
    filters,
    dashboardSummary: false,
    includeSectionDimension: true,
    order,
    enabled: !isResourceFiltering,
  });

  // Query with tag filtering
  const {
    data: currDataByTag,
    isLoading: isLoadingCurrByTag,
    error: errorCurrByTag,
  } = useCostSavingOpportunityTag({
    dashboardKey,
    dateRange: "Yesterday",
    filters,
    dashboardSummary: false,
    includeSectionDimension: true,
    order,
    enabled: isResourceFiltering,
  });

  const {
    data: prevDataByTag,
    isLoading: isLoadingPrevByTag,
    error: errorPrevByTag,
  } = useCostSavingOpportunityTag({
    dashboardKey,
    dateRange: [EOM_DATE_STR, EOM_DATE_STR],
    filters,
    dashboardSummary: false,
    includeSectionDimension: true,
    order,
    enabled: isResourceFiltering,
  });

  const results = useMemo(() => {
    const isLoading = isResourceFiltering
      ? isLoadingCurrByTag || isLoadingPrevByTag
      : isLoadingCurrNoTag || isLoadingPrevNoTag;

    if (isLoading) {
      return null;
    }

    const currData = isResourceFiltering ? currDataByTag : currDataNoTag;
    const prevData = isResourceFiltering ? prevDataByTag : prevDataNoTag;

    return processSectionData(
      currData,
      prevData,
      costType,
      isResourceFiltering,
      aggregateBySectionKey,
      sortBy,
    );
  }, [
    currDataNoTag,
    prevDataNoTag,
    currDataByTag,
    prevDataByTag,
    costType,
    isResourceFiltering,
    aggregateBySectionKey,
    sortBy,
    isLoadingCurrNoTag,
    isLoadingPrevNoTag,
    isLoadingCurrByTag,
    isLoadingPrevByTag,
  ]);

  const combinedError = useMemo(() => {
    const currError = isResourceFiltering ? errorCurrByTag : errorCurrNoTag;
    const prevError = isResourceFiltering ? errorPrevByTag : errorPrevNoTag;

    if (currError && prevError) {
      // Both queries failed - combine error messages for better debugging
      return new Error(
        `Multiple query failures - Current: ${currError.message}, Previous: ${prevError.message}`,
      );
    }
    if (currError) {
      return new Error(`Current data query failed: ${currError.message}`);
    }
    if (prevError) {
      return new Error(`Previous data query failed: ${prevError.message}`);
    }
    return null;
  }, [
    errorCurrNoTag,
    errorPrevNoTag,
    errorCurrByTag,
    errorPrevByTag,
    isResourceFiltering,
  ]);

  return {
    results,
    isLoading: isResourceFiltering
      ? isLoadingCurrByTag || isLoadingPrevByTag
      : isLoadingCurrNoTag || isLoadingPrevNoTag,
    error: combinedError,
  };
}
